/**
 * Creators' submissions to a campaign: data and actions for the campaign page.
 *
 * The API runs every intake check (specification §6.4) and records each submission, accepted or
 * rejected. It also checks an accepted X post at once against the brief's rules (§8.1, check 1):
 * a post without a required element, or a repost, stays an item but will fail. The page shows
 * each outcome in the creator's words.
 *
 * A YouTube submission includes its source video (§8.3): the browser asks the API for an upload
 * link, uploads the file straight to Morcast's storage with it, then submits the video's address
 * with the upload's name, and for an INTEGRATION campaign the branded segment. An upload stays
 * until a submission is recorded with it, so a submission that fails before that, for example
 * while YouTube cannot be reached, is tried again with the same upload. Each new upload takes one
 * of the wallet's five upload slots in the campaign until it is submitted or deleted.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { ApiRequestError, api } from "../api/client.js";
import type { CampaignPhase, PublicCampaign } from "./campaigns.js";

/**
 * A check of an accepted item, as the API describes it: those of intake at once, the others as
 * Morcast runs them (specification §8). `review` waits for a person's decision.
 */
export interface SubmissionCheck {
  check:
    | "edits"
    | "rules"
    | "elements"
    | "media"
    | "format"
    | "manipulation"
    | "semantic"
    | "visual"
    | "fraud";
  outcome: "pass" | "fail" | "review";
  /** The reason codes of a failure, such as `MISSING_ELEMENT`. */
  reasons: string[];
  details: {
    /** The required elements the post lacks. */
    missing?: { kind: "link" | "hashtag" | "mention" | "text" | "disclosure"; value: string }[];
    /** The post a repost reposts. */
    repostOf?: string;
    /** The brief's requirements, each with the model's outcome. */
    requirements?: { requirement?: string; outcome?: string }[];
    /** The brief's prohibitions, each with the model's outcome. */
    prohibitions?: { prohibition?: string; outcome?: string }[];
  };
}

/** A submission, as the API describes it. */
export interface Submission {
  id: number;
  status: "accepted" | "rejected" | "withdrawn";
  /** The intake check that rejected it. */
  reason: string | null;
  url: string;
  contentId: string | null;
  /** The wallet's account that published the content. Null when intake never knew it. */
  accountId: string | null;
  postedAt: string | null;
  receivedAt: string;
  withdrawnAt: string | null;
  /** YouTube: the uploaded video's size in bytes, and an INTEGRATION video's segment. */
  mediaBytes: number | null;
  segment: { start: number; end: number } | null;
  /** The checks of an accepted post so far, in the order of the specification. */
  checks: SubmissionCheck[];
}

/** A missing element, as the creator reads it: "#ad", "the link https://…". */
function describeElement(element: { kind: string; value: string }): string {
  switch (element.kind) {
    case "link":
      return `the link ${element.value}`;
    case "text":
      return `the phrase "${element.value}"`;
    case "disclosure":
      return `the disclosure "${element.value}"`;
    default:
      return element.value;
  }
}

/** "a", "a and b", "a, b and c". */
function listed(items: string[]): string {
  return items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items.at(-1)}`;
}

/** What each failure reason means for the creator, where the check's details say no more. */
const FAILURES: Record<string, string> = {
  EDITED: "It was changed after you submitted it.",
  DUPLICATE: "It is a repost, which only counts the original post again.",
  MISSING_ELEMENT: "It lacks an element the brief requires.",
  MEDIA_MISMATCH: "The file you uploaded is not the video you published.",
  NOT_DEDICATED: "The brand is not the main subject of the video.",
  PROMOTION_NOT_IN_SEGMENT: "The segment you declared does not contain the promotion.",
  SILENT_CONTENT: "It is mostly silent or music only, so it cannot be checked against the brief.",
  MANIPULATION: "It tries to influence Morcast's evaluation.",
  BRIEF_NOT_MET: "It does not meet the brief.",
  FRAUD: "Its reach includes fraudulent activity.",
};

/** The brief's requirements and prohibitions a check found broken, one sentence each. */
function briefProblems(details: SubmissionCheck["details"]): string[] {
  const unmet = (details.requirements ?? [])
    .filter((entry) => entry.outcome === "FAIL" && entry.requirement)
    .map((entry) => `It does not meet the requirement "${entry.requirement}".`);
  const shown = (details.prohibitions ?? [])
    .filter((entry) => entry.outcome === "FAIL" && entry.prohibition)
    .map((entry) => `It shows "${entry.prohibition}", which the brief does not allow.`);
  return [...unmet, ...shown];
}

/**
 * What the checks found wrong with an accepted item so far, one sentence each, in the creator's
 * words, in the order of the checks. Empty when none failed. A failure is final unless an issue
 * raised in the result's review is upheld.
 */
export function checkProblems(submission: Pick<Submission, "checks">): string[] {
  const problems: string[] = [];
  for (const check of submission.checks) {
    if (check.outcome !== "fail") {
      continue;
    }
    if (check.check === "elements" && check.details.missing?.length) {
      problems.push(`It lacks ${listed(check.details.missing.map(describeElement))}.`);
      continue;
    }
    const brief =
      check.check === "semantic" || check.check === "visual" ? briefProblems(check.details) : [];
    if (brief.length > 0) {
      problems.push(...brief);
      continue;
    }
    for (const reason of check.reasons) {
      problems.push(FAILURES[reason] ?? `It failed a check (${reason}).`);
    }
  }
  return [...new Set(problems)];
}

/** Whether a person at Morcast is deciding one of the item's checks. */
export function inReview(submission: Pick<Submission, "checks">): boolean {
  return submission.checks.some((check) => check.outcome === "review");
}

/** A submission with its campaign, as `GET /api/submissions` lists the wallet's submissions. */
export interface WalletSubmission extends Submission {
  campaign: {
    escrow: string;
    id: string;
    title: string;
    brand: string;
    platform: "X" | "YOUTUBE";
    format: "DEDICATED" | "INTEGRATION" | null;
    phase: CampaignPhase;
    startAt: string;
    endAt: string;
  };
}

/** The signed-in wallet's submissions to a campaign, and its slots. */
export interface MySubmissions {
  submissions: Submission[];
  slots: {
    /** The most submissions each account may have here, accepted or withdrawn. */
    limit: number;
    /** The most accounts the wallet may take part with here. */
    accountLimit: number;
    /** The wallet's accounts that took part here, with the slots each used. */
    accounts: { accountId: string; used: number }[];
  };
}

/** What each intake rejection means for the creator, on each platform. */
const REJECTIONS: Record<"X" | "YOUTUBE", Record<string, string>> = {
  X: {
    WRONG_PLATFORM: "That address is not an X post.",
    NOT_FOUND: "X has no post at that address.",
    NOT_PUBLIC: "The post is not public.",
    NOT_OWNER: "The post was not written by any of your linked X accounts.",
    ACCOUNT_MISMATCH: "This X account already takes part in this campaign from another wallet.",
    ACCOUNT_LIMIT:
      "You already take part in this campaign with the most accounts it allows. Submit from one of them.",
    OUT_OF_WINDOW: "The post was published before the campaign started.",
    DUPLICATE: "The post is already part of a Morcast campaign.",
    OVER_LIMIT: "This account has used all its submissions for this campaign.",
  },
  YOUTUBE: {
    WRONG_PLATFORM: "That address is not a YouTube video.",
    NOT_FOUND: "YouTube has no video at that address.",
    NOT_PUBLIC: "The video is not public.",
    UNSUPPORTED_FORMAT: "Only standard uploads take part, not Shorts or live streams.",
    NOT_OWNER: "The video is not on any of your linked YouTube channels.",
    ACCOUNT_MISMATCH: "This channel already takes part in this campaign from another wallet.",
    ACCOUNT_LIMIT:
      "You already take part in this campaign with the most channels it allows. Submit from one of them.",
    OUT_OF_WINDOW: "The video was published before the campaign started.",
    DUPLICATE: "The video is already part of a Morcast campaign.",
    OVER_LIMIT: "This channel has used all its submissions for this campaign.",
  },
};

/** Why a submission was rejected, in the creator's words. */
export function rejectionText(reason: string | null, platform: "X" | "YOUTUBE"): string {
  return REJECTIONS[platform][reason ?? ""] ?? reason ?? "";
}

function submissionsPath(campaign: Pick<PublicCampaign, "escrow" | "id">): string {
  return `/api/campaigns/${campaign.escrow}/${campaign.id}/submissions`;
}

/** Query keys of submission data start with this. A change refreshes all of them. */
const SUBMISSIONS_KEY = ["submissions"] as const;

function queryKey(campaign: Pick<PublicCampaign, "escrow" | "id">) {
  return [...SUBMISSIONS_KEY, campaign.escrow, campaign.id] as const;
}

/** The signed-in wallet's submissions in every campaign, newest first. */
export function useAllMySubmissions() {
  return useQuery({
    queryKey: [...SUBMISSIONS_KEY, "all"] as const,
    queryFn: () => api.get<{ submissions: WalletSubmission[] }>("/api/submissions"),
    select: (answer) => answer.submissions,
  });
}

/** The signed-in wallet's submissions to the campaign. */
export function useMySubmissions(
  campaign: Pick<PublicCampaign, "escrow" | "id">,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKey(campaign),
    queryFn: () => api.get<MySubmissions>(submissionsPath(campaign)),
    enabled,
  });
}

/** Submits content to the campaign. The answer is the recorded submission. */
export function useSubmit(campaign: Pick<PublicCampaign, "escrow" | "id">) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (url: string) => api.post<Submission>(submissionsPath(campaign), { url }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY }),
  });
}

/** Where the browser uploads a video, as `POST …/uploads` answers. */
interface UploadTarget {
  upload: string;
  url: string;
  method: "PUT";
  headers: Record<string, string>;
}

/** A file as the browser knows it, to tell whether a creator chose the same one again. */
function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * Submits a YouTube video: uploads the file, then submits its address with the upload. `progress`
 * is the share of the file uploaded so far, from 0 to 1, while the upload runs. Null otherwise,
 * also while the API checks the video.
 *
 * The upload is kept for the next try until a submission is recorded with it, accepted or
 * rejected, after which the API deletes it. A try with the same file reuses it instead of
 * uploading again, and an upload the API no longer has is uploaded once more.
 */
export function useSubmitVideo(campaign: Pick<PublicCampaign, "escrow" | "id">) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const uploaded = useRef<{ file: string; upload: string } | null>(null);

  async function upload(file: File): Promise<string> {
    const target = await api.post<UploadTarget>(
      `/api/campaigns/${campaign.escrow}/${campaign.id}/uploads`,
      { contentType: file.type || "video/mp4", size: file.size },
    );
    setProgress(0);
    await uploadFile(target, file, setProgress);
    // Uploaded: the API checks the video now.
    setProgress(null);
    uploaded.current = { file: fileKey(file), upload: target.upload };
    return target.upload;
  }

  const mutation = useMutation({
    mutationFn: async (input: {
      url: string;
      file: File;
      segment?: { start: number; end: number };
    }) => {
      const submit = (name: string) =>
        api.post<Submission>(submissionsPath(campaign), {
          url: input.url,
          upload: name,
          ...(input.segment ? { segment: input.segment } : {}),
        });
      const kept = uploaded.current?.file === fileKey(input.file) ? uploaded.current.upload : null;
      let recorded: Submission;
      try {
        recorded = await submit(kept ?? (await upload(input.file)));
      } catch (error) {
        if (!(kept && error instanceof ApiRequestError && error.code === "UPLOAD_NOT_FOUND")) {
          throw error;
        }
        // The kept upload expired: upload the file again, once.
        uploaded.current = null;
        recorded = await submit(await upload(input.file));
      }
      // The API deleted the upload with the recorded submission.
      uploaded.current = null;
      return recorded;
    },
    onSettled: () => {
      setProgress(null);
      return queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY });
    },
  });
  return { ...mutation, progress };
}

/**
 * How long a video file lasts, in whole seconds, as the browser reads it. Null when the browser
 * cannot read it, such as for a format it does not play. The API checks the published video
 * anyway: this only lets the form catch a segment past the end before uploading.
 */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const done = (duration: number | null) => {
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      resolve(duration);
    };
    video.preload = "metadata";
    video.onloadedmetadata = () =>
      done(Number.isFinite(video.duration) ? Math.floor(video.duration) : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

/** A time in a video in whole seconds, as people write it: "1:30", "1:02:05". */
export function formatVideoTime(seconds: number): string {
  const hours = Math.floor(seconds / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}

/**
 * Uploads a file through a signed link, reporting progress. The headers are part of the link's
 * signature, so they are sent exactly as given.
 */
function uploadFile(
  target: UploadTarget,
  file: File,
  onProgress: (share: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(target.method, target.url);
    for (const [name, value] of Object.entries(target.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(
            new ApiRequestError(
              request.status,
              "UPLOAD_FAILED",
              "The video could not be uploaded. Please try again.",
            ),
          );
    request.onerror = () =>
      reject(
        new ApiRequestError(
          0,
          "UPLOAD_FAILED",
          "The upload stopped. Check your connection and try again.",
        ),
      );
    request.send(file);
  });
}

/**
 * A time in a video as the creator writes it, "1:30" or "1:02:05", in whole seconds. Null when
 * it is not a time.
 */
export function parseVideoTime(text: string): number | null {
  const match = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) {
    return null;
  }
  const [hours, minutes, seconds] = [Number(match[1] ?? 0), Number(match[2]), Number(match[3])];
  return seconds < 60 && (match[1] === undefined || minutes < 60)
    ? hours * 3_600 + minutes * 60 + seconds
    : null;
}

/** Withdraws an accepted submission. */
export function useWithdraw() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Submission>(`/api/submissions/${id}/withdraw`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SUBMISSIONS_KEY }),
  });
}
